import React, { useEffect, useState } from 'react';
import {
  RaceObservation,
  EntityNote,
  getRaceObservations,
  getEntityNotes,
} from '../api/voice';
import './ClassifiedNotes.css';

type Props =
  | { mode: 'race'; meetingName: string; raceNumber: number | string; date?: string }
  | { mode: 'horse'; horseName: string };

// Surfaces the classifier's committed records for a single race or horse —
// RACE_OBSERVATIONs sorted by race, ENTITY_NOTEs sorted by horse.
const ClassifiedNotes: React.FC<Props> = (props) => {
  const [observations, setObservations] = useState<RaceObservation[]>([]);
  const [notes, setNotes] = useState<EntityNote[]>([]);

  const key =
    props.mode === 'race'
      ? `race:${props.meetingName}:${props.raceNumber}:${props.date || ''}`
      : `horse:${props.horseName}`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (props.mode === 'race') {
          const data = await getRaceObservations(props.meetingName, props.raceNumber, props.date);
          if (!cancelled) setObservations(data);
        } else {
          const data = await getEntityNotes(props.horseName);
          if (!cancelled) setNotes(data);
        }
      } catch {
        // Non-fatal: panel just stays empty.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const empty = props.mode === 'race' ? observations.length === 0 : notes.length === 0;
  if (empty) return null;

  return (
    <div className="cnotes">
      <div className="cnotes-head">
        {props.mode === 'race' ? 'Race observations' : 'Notes on this horse'}
        <span className="cnotes-sub">from voice notes</span>
      </div>
      <ul className="cnotes-list">
        {props.mode === 'race'
          ? observations.map((o) => (
              <li key={o.id} className="cnotes-item">
                <span className="cnotes-text">{o.observation}</span>
                {o.tags?.length > 0 && (
                  <span className="cnotes-tags">
                    {o.tags.map((t) => (
                      <span key={t} className="cnotes-tag">
                        {t}
                      </span>
                    ))}
                  </span>
                )}
                {o.shared && (
                  <span className="cnotes-shared" title="From an admin-approved shared note">
                    shared{o.username ? ` · ${o.username}` : ''}
                  </span>
                )}
              </li>
            ))
          : notes.map((n) => (
              <li key={n.id} className="cnotes-item">
                <span className={`cnotes-etype cnotes-etype--${n.entity_type}`}>{n.entity_type}</span>
                <span className="cnotes-text">{n.trait}</span>
                {!n.resolved && (
                  <span className="cnotes-unresolved" title="Entity not yet confirmed in the registry">
                    unconfirmed
                  </span>
                )}
                {n.shared && (
                  <span className="cnotes-shared" title="From an admin-approved shared note">
                    shared{n.username ? ` · ${n.username}` : ''}
                  </span>
                )}
              </li>
            ))}
      </ul>
    </div>
  );
};

export default ClassifiedNotes;
